import { __assign, __read, __values } from "tslib";
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { noop, useDrag } from '../Drawer.utils';
import { cnDrawerContent, cnDrawerCurtain, cnDrawerDragObserver, cnDrawerHandle, cnDrawerOverlay, cnDrawerTitle, } from '../Drawer.const';
function getSpringTransform(_a) {
    var axis = _a.axis, springValue = _a.springValue, inverted = _a.inverted;
    if (springValue === 1) {
        return null;
    }
    return axis === 'x'
        ? "translate3d(" + (1 - springValue) * 100 * inverted + "%,0,0)"
        : "translate3d(0," + (1 - springValue) * 100 * inverted + "%,0)";
}
function getFirstInteractiveElementInScope(scopeRef) {
    var e_1, _a;
    var _b;
    var interactiveElements = Array.from(scopeRef.querySelectorAll('a[href], button, input, select, textarea'));
    try {
        for (var interactiveElements_1 = __values(interactiveElements), interactiveElements_1_1 = interactiveElements_1.next(); !interactiveElements_1_1.done; interactiveElements_1_1 = interactiveElements_1.next()) {
            var element = interactiveElements_1_1.value;
            var computedStyle = window.getComputedStyle(element);
            var isHidden = element.getAttribute('aria-hidden') === 'true' || (computedStyle === null || computedStyle === void 0 ? void 0 : computedStyle.display) === 'none';
            var hasAriaHiddenParent = element.closest('[aria-hidden="true"]') !== null;
            var hasDisplayHiddenParent = element.closest('[style*="display: none"]') !== null;
            var hasHiddenParent = hasAriaHiddenParent || hasDisplayHiddenParent;
            var hasText = ((_b = element === null || element === void 0 ? void 0 : element.textContent) === null || _b === void 0 ? void 0 : _b.trim()) !== '';
            if (!isHidden && !hasHiddenParent && (hasText || (element === null || element === void 0 ? void 0 : element.tagName) === 'BUTTON')) {
                return element;
            }
        }
    }
    catch (e_1_1) { e_1 = { error: e_1_1 }; }
    finally {
        try {
            if (interactiveElements_1_1 && !interactiveElements_1_1.done && (_a = interactiveElements_1.return)) _a.call(interactiveElements_1);
        }
        finally { if (e_1) throw e_1.error; }
    }
    return;
}
/**
 * Компонент содержимого шторки.
 *
 * При использовании Popup мы не знаем в какой конкретно
 * момент содержимое монтируется/размонтируется, если
 * прокидывать его напрямую без обёрток.
 */
export var DrawerContent = function (_a) {
    var dragDisabled = _a.dragDisabled, visible = _a.visible, _b = _a.onClose, onClose = _b === void 0 ? noop : _b, _c = _a.onCloseEnd, onCloseEnd = _c === void 0 ? noop : _c, springValue = _a.springValue, _d = _a.direction, direction = _d === void 0 ? 'bottom' : _d, maxSize = _a.maxSize, titleComponent = _a.titleComponent, children = _a.children, setSpringDisabled = _a.setSpringDisabled, setProgress = _a.setProgress, _e = _a.autoFocus, autoFocus = _e === void 0 ? false : _e;
    var contentRef = useRef(null);
    var _f = __read(useState(false), 2), closing = _f[0], setClosing = _f[1];
    var axis = direction === 'bottom' ? 'y' : 'x';
    var inverted = direction === 'left' ? -1 : 1;
    var springOpacity = Math.max(springValue, 0);
    var springTransform = getSpringTransform({ axis: axis, springValue: springValue, inverted: inverted });
    var curtainRef = useRef(null);
    useEffect(function () {
        var _a;
        if (visible && autoFocus && springValue === 1) {
            var element = (curtainRef === null || curtainRef === void 0 ? void 0 : curtainRef.current) && getFirstInteractiveElementInScope(curtainRef.current);
            if (element) {
                element.focus();
                /**
                 * Если drawer открывается дольше 600 мс, то при открытии шторки
                 * элемент на котором сейчас находится фокус имеет обводку (outline).
                 */
                document.body.classList.remove('utilityfocus');
                document.body.classList.add('pointerfocus');
            }
            else {
                (_a = curtainRef.current) === null || _a === void 0 ? void 0 : _a.focus();
            }
        }
        // TODO ISL-10952: разобраться почему не все необходимые переменные указаны в deps и убрать игнор
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [visible, springValue]);
    var curtainStyle = useMemo(function () {
        var _a;
        return (__assign(__assign({}, (springTransform ? { transform: springTransform } : {})), (maxSize && (_a = {}, _a[axis === 'x' ? 'maxWidth' : 'maxHeight'] = maxSize, _a))));
    }, [springTransform, maxSize, axis]);
    useEffect(function () {
        if (closing && springValue === 0) {
            onCloseEnd();
            setClosing(false);
        }
    }, [closing, springValue, onCloseEnd]);
    var _onClose = useCallback(function () {
        setClosing(true);
        onClose();
    }, [onClose]);
    /**
     * Обработчик drag событий с корневого DOM элемента шторки
     */
    var dragProps = useDrag(function (dragState) {
        if (!visible || !contentRef.current)
            return;
        var _a = dragState.velocity, vx = _a.x, vy = _a.y, _b = dragState.movement, mx = _b.x, my = _b.y, first = dragState.first, last = dragState.last, data = dragState.data, event = dragState.event;
        var drawerSize = axis === 'x' ? contentRef.current.clientWidth : contentRef.current.clientHeight;
        var movement = inverted * (axis === 'x' ? mx : my);
        var velocity = inverted * (axis === 'x' ? vx : vy);
        if (first) {
            data.isTargetUnderContent = contentRef.current.contains(event.target);
            data.initialScrollPosition = contentRef.current.scrollTop;
            data.isScrolled = data.initialScrollPosition !== 0;
        }
        else {
            data.isScrolled = data.isScrolled || data.initialScrollPosition - contentRef.current.scrollTop < 0;
        }
        // ничего не делаем когда жест происходит одновременно с проскроллом
        // или если шторка в статичном состоянии
        // @see SERP-107544
        if (dragDisabled || (data.isTargetUnderContent && data.isScrolled)) {
            return;
        }
        // жест завершен, возвращаем шторку в открытое положение, если
        // скорость была недостаточной и, закрываем если наоборот
        if (last) {
            setSpringDisabled(false);
            if (Math.abs(velocity) >= 0.1) {
                return velocity > 0 ? _onClose() : setProgress(1);
            }
            else if (movement / drawerSize >= 0.3) {
                return _onClose();
            }
            return setProgress(1);
        }
        if (movement > 0) {
            setSpringDisabled(true);
            var progress = Math.max(0, 1 - movement / drawerSize);
            if (progress === 0) {
                return _onClose();
            }
            return setProgress(progress);
        }
    }).dragProps;
    return (React.createElement("div", __assign({ className: cnDrawerDragObserver }, dragProps),
        React.createElement("div", { className: cnDrawerOverlay, style: { opacity: springOpacity }, onClick: function () { return _onClose(); } }),
        React.createElement("div", { className: cnDrawerCurtain, style: curtainStyle, ref: curtainRef, tabIndex: -1, role: "dialog", "aria-modal": springValue === 1 },
            React.createElement("div", { className: cnDrawerHandle, style: { opacity: springOpacity } }),
            titleComponent && React.createElement("div", { className: cnDrawerTitle }, titleComponent),
            React.createElement("div", { className: cnDrawerContent, ref: contentRef }, children))));
};
